import { Mesh, Texture } from 'three';
import type { Group, Material } from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

/** Owns an embedded-texture GLB and its GPU resources for one race. */
export class ModelAsset {
  constructor(readonly root: Group) {}

  static async load(bytes: ArrayBuffer, signal: AbortSignal): Promise<ModelAsset> {
    const gltf = await new GLTFLoader().parseAsync(bytes, '');
    const asset = new ModelAsset(gltf.scene);
    if (signal.aborted) {
      asset.dispose();
      signal.throwIfAborted();
    }
    return asset;
  }

  dispose(): void {
    const resources = new Set<{ dispose(): void }>();
    this.root.traverse((object) => {
      if (!(object instanceof Mesh)) return;
      resources.add(object.geometry);
      const materials: Material[] = Array.isArray(object.material)
        ? object.material
        : [object.material];
      for (const material of materials) {
        resources.add(material);
        for (const value of Object.values(material)) {
          if (!(value instanceof Texture)) continue;
          resources.add(value);
          // GLTFLoader uses ImageBitmap when available; disposing a texture alone
          // does not release the decoded bitmap.
          if (typeof ImageBitmap !== 'undefined' && value.image instanceof ImageBitmap)
            value.image.close();
        }
      }
    });
    for (const resource of resources) resource.dispose();
  }
}
